import { ChangeDetectorRef, OnDestroy, Pipe, PipeTransform, inject } from '@angular/core';
import { formatDate } from '@angular/common';
import { Subscription } from 'rxjs';
import { LocaleService, DEFAULT_LANG } from '../services/locale.service';

/**
 * DatePipe resolves LOCALE_ID once, when the injector is created, so it cannot follow a
 * runtime language switch. This calls formatDate() with the locale held in LocaleService
 * instead, and marks itself dirty when the language changes.
 *
 * Only needed for patterns with month/weekday names ('EEE, dd MMM'). Purely numeric
 * patterns ('dd.MM.yyyy HH:mm:ss') render the same in every locale — leave those on DatePipe.
 */
@Pipe({ name: 'localizedDate', standalone: true, pure: false })
export class LocalizedDatePipe implements PipeTransform, OnDestroy {
  private locale = inject(LocaleService);
  private cdr = inject(ChangeDetectorRef);
  private sub: Subscription;

  constructor() {
    this.sub = this.locale.lang$.subscribe(() => this.cdr.markForCheck());
  }

  transform(value: Date | string | number | null | undefined, format = 'mediumDate'): string {
    if (value === null || value === undefined || value === '') return '';
    try {
      return formatDate(value, format, this.locale.current);
    } catch {
      // registerLocaleData missing for this language, or an unparseable value.
      return formatDate(value, format, DEFAULT_LANG);
    }
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }
}
